import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { projectsAPI } from '../utils/api';
import { toast } from '../hooks/use-toast';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      try {
        const response = await projectsAPI.getById(id);
        setProject(response.data.project);
        
        // Fetch related projects
        const allProjects = await projectsAPI.getAll(response.data.project.category);
        const related = allProjects.data.projects
          .filter(p => p.id !== id)
          .slice(0, 3);
        setRelatedProjects(related);
      } catch (error) {
        // Error handling - consider using error logging service in production
        toast({
          title: "Error",
          description: "Failed to load project details.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Re-fetch when project ID changes

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-500 text-lg">Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h2>
          <Link to="/projects">
            <button className="text-gray-900 hover:text-gray-600 transition-colors">Back to Projects</button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <section className="pt-32 pb-8 px-8">
        <div className="max-w-7xl mx-auto">
          <Link to="/projects">
            <button className="text-gray-900 hover:text-gray-600 transition-colors inline-flex items-center gap-2 text-base">
              <ArrowLeft className="w-5 h-5" />
              Back to Work
            </button>
          </Link>
        </div>
      </section>

      {/* Hero Image */}
      <section className="px-8 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="relative h-[75vh] overflow-hidden mb-8">
            <img
              src={project.gallery[selectedImage]}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Image Thumbnails */}
          {project.gallery.length > 1 && (
            <div className="flex gap-4 overflow-x-auto">
              {project.gallery.map((img, index) => (
                <button
                  key={`gallery-${project.id}-${index}`}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-24 h-20 overflow-hidden border-2 transition-all ${
                    selectedImage === index
                      ? 'border-gray-900'
                      : 'border-gray-200 opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Project Details */}
      <section className="py-16 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Main Content */}
            <div className="space-y-8">
              <div>
                <p className="text-sm uppercase tracking-wider text-gray-500 mb-4">
                  {project.category}
                </p>
                <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  {project.title}
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {project.description}
                </p>
              </div>
            </div>

            {/* Sidebar Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Client</h3>
                <p className="text-lg text-gray-900">{project.client}</p>
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Location</h3>
                <p className="text-lg text-gray-900">{project.location}</p>
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Completion</h3>
                <p className="text-lg text-gray-900">{project.year}</p>
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Area</h3>
                <p className="text-lg text-gray-900">{project.area}</p>
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Services</h3>
                <ul className="space-y-2">
                  {project.services.map((service) => (
                    <li key={service} className="text-lg text-gray-900">{service}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="py-24 px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12">More Projects</h2>
            <div className="space-y-12">
              {relatedProjects.map((relatedProject) => (
                <Link key={relatedProject.id} to={`/projects/${relatedProject.id}`}>
                  <div className="group cursor-pointer">
                    <div className="relative h-[50vh] overflow-hidden mb-6">
                      <img
                        src={relatedProject.image}
                        alt={relatedProject.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="max-w-2xl">
                      <p className="text-sm uppercase tracking-wider text-gray-500 mb-3">
                        {relatedProject.category}
                      </p>
                      <h3 className="text-3xl font-bold text-gray-900 mb-3 group-hover:text-gray-600 transition-colors">
                        {relatedProject.title}
                      </h3>
                      <p className="text-lg text-gray-600">{relatedProject.location}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProjectDetail;